import { z } from 'zod'
import { workspaceFileIdSchema } from '@/lib/api/contracts/primitives'
import {
  type ContractBodyInput,
  type ContractJsonResponse,
  type ContractParamsInput,
  type ContractQueryInput,
  defineRouteContract,
} from '@/lib/api/contracts/types'
import { FileInputSchema } from '@/lib/uploads/utils/file-schemas'

const jsonResponseSchema = z.unknown()

const connectionFields = {
  host: z.string().min(1, 'Host is required'),
  port: z.coerce.number().int().positive().default(22),
  username: z.string().min(1, 'Username is required'),
  password: z.string().nullish(),
  privateKey: z.string().nullish(),
  passphrase: z.string().nullish(),
}

export function requirePasswordOrPrivateKey<S extends z.ZodType>(schema: S): S {
  return schema.refine(
    (value) => {
      const connection = value as { password?: string | null; privateKey?: string | null }
      return Boolean(connection.password || connection.privateKey)
    },
    { message: 'Either password or privateKey must be provided' }
  ) as S
}

export const jupyterUploadBodySchema = z.object({
  serverUrl: z.string().min(1, 'Server URL is required'),
  token: z.string().min(1, 'Token is required'),
  directory: z.string().optional().nullable(),
  file: FileInputSchema.optional().nullable(),
  fileContent: z.string().optional().nullable(),
  fileName: z.string().optional().nullable(),
})

export const sshCheckCommandExistsBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    commandName: z.string().min(1, 'Command name is required'),
  })
)

export const sshCheckFileExistsBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    path: z.string().min(1, 'Path is required'),
    type: z.enum(['file', 'directory', 'any']).default('any'),
  })
)

export const sshCreateDirectoryBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    path: z.string().min(1, 'Path is required'),
    recursive: z.boolean().default(true),
    permissions: z.string().default('0755'),
  })
)

export const sshDeleteFileBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    path: z.string().min(1, 'Path is required'),
    recursive: z.boolean().default(false),
    force: z.boolean().default(false),
  })
)

export const sshDownloadFileBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    remotePath: z.string().min(1, 'Remote path is required'),
  })
)

export const sshExecuteCommandBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    command: z.string().min(1, 'Command is required'),
    workingDirectory: z.string().nullish(),
  })
)

export const sshExecuteScriptBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    script: z.string().min(1, 'Script content is required'),
    interpreter: z.string().default('/bin/bash'),
    workingDirectory: z.string().nullish(),
  })
)

export const sshGetSystemInfoBodySchema = requirePasswordOrPrivateKey(z.object(connectionFields))

export const sshListDirectoryBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    path: z.string().min(1, 'Path is required'),
    detailed: z.boolean().default(true),
    recursive: z.boolean().default(false),
  })
)

export const sshMoveRenameBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    sourcePath: z.string().min(1, 'Source path is required'),
    destinationPath: z.string().min(1, 'Destination path is required'),
    overwrite: z.boolean().default(false),
  })
)

export const sshReadFileContentBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    path: z.string().min(1, 'Path is required'),
    encoding: z.string().default('utf-8'),
    maxSize: z.coerce.number().min(0.01).max(50).default(10),
  })
)

export const sshUploadFileBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    fileContent: z.string().min(1, 'File content is required'),
    fileName: z.string().min(1, 'File name is required'),
    remotePath: z.string().min(1, 'Remote path is required'),
    permissions: z.string().nullish(),
    overwrite: z.boolean().default(true),
  })
)

export const sshWriteFileContentBodySchema = requirePasswordOrPrivateKey(
  z.object({
    ...connectionFields,
    path: z.string().min(1, 'Path is required'),
    content: z.string(),
    mode: z.enum(['overwrite', 'append', 'create']).default('overwrite'),
    permissions: z.string().nullish(),
  })
)

export const storageContextSchema = z.enum([
  'knowledge-base',
  'chat',
  'copilot',
  'mothership',
  'execution',
  'workspace',
  'profile-pictures',
  'og-images',
  'logs',
  'workspace-logos',
  'organization-logos',
])

export const fileParseBodySchema = z
  .object({
    filePath: z
      .union([z.string(), z.array(z.string()).max(10, 'At most 10 files can be parsed at once')])
      .optional(),
    fileType: z.string().optional().default(''),
    headers: z.record(z.string(), z.string()).optional(),
    workspaceId: z.string().optional().default(''),
    workflowId: z.string().optional(),
    executionId: z.string().optional(),
  })
  .passthrough()

export const fileDeleteBodySchema = z
  .object({
    filePath: z.string().optional(),
    context: storageContextSchema.optional(),
  })
  .passthrough()

export const fileServeParamsSchema = z.object({
  path: z.array(z.string()).min(1),
})

export const fileServeQuerySchema = z.object({
  raw: z.string().nullish(),
  /** `1` => rendering, not downloading — a HEIC may be substituted with a JPEG derivative. */
  preview: z.string().nullish(),
  /** Content version (the file record's `updatedAt`). Present => the URL is content-immutable and may be cached indefinitely by the browser. */
  v: z.string().nullish(),
})

export const fileViewParamsSchema = z.object({
  id: workspaceFileIdSchema,
})

export const fileExportParamsSchema = z.object({
  id: workspaceFileIdSchema,
})

export const jupyterUploadContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/jupyter/upload',
  body: jupyterUploadBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshCheckCommandExistsContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/check-command-exists',
  body: sshCheckCommandExistsBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshCheckFileExistsContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/check-file-exists',
  body: sshCheckFileExistsBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshCreateDirectoryContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/create-directory',
  body: sshCreateDirectoryBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshDeleteFileContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/delete-file',
  body: sshDeleteFileBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshDownloadFileContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/download-file',
  body: sshDownloadFileBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshExecuteCommandContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/execute-command',
  body: sshExecuteCommandBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshExecuteScriptContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/execute-script',
  body: sshExecuteScriptBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshGetSystemInfoContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/get-system-info',
  body: sshGetSystemInfoBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshListDirectoryContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/list-directory',
  body: sshListDirectoryBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshMoveRenameContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/move-rename',
  body: sshMoveRenameBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshReadFileContentContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/read-file-content',
  body: sshReadFileContentBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshUploadFileContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/upload-file',
  body: sshUploadFileBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const sshWriteFileContentContract = defineRouteContract({
  method: 'POST',
  path: '/api/tools/ssh/write-file-content',
  body: sshWriteFileContentBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const fileParseContract = defineRouteContract({
  method: 'POST',
  path: '/api/files/parse',
  body: fileParseBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const fileDeleteContract = defineRouteContract({
  method: 'POST',
  path: '/api/files/delete',
  body: fileDeleteBodySchema,
  response: { mode: 'json', schema: jsonResponseSchema },
})

export const fileServeContract = defineRouteContract({
  method: 'GET',
  path: '/api/files/serve/[...path]',
  params: fileServeParamsSchema,
  query: fileServeQuerySchema,
  response: { mode: 'binary' },
})

export const fileViewContract = defineRouteContract({
  method: 'GET',
  path: '/api/files/view/[id]',
  params: fileViewParamsSchema,
  response: { mode: 'binary' },
})

export const fileExportContract = defineRouteContract({
  method: 'GET',
  path: '/api/files/export/[id]',
  params: fileExportParamsSchema,
  response: { mode: 'binary' },
})

export const fileStorageStatusResponseSchema = z.object({
  cloudConfigured: z.boolean(),
})

export const fileStorageStatusContract = defineRouteContract({
  method: 'GET',
  path: '/api/files/storage-status',
  response: { mode: 'json', schema: fileStorageStatusResponseSchema },
})

export type FileStorageStatusResponse = ContractJsonResponse<typeof fileStorageStatusContract>

export type JupyterUploadBody = ContractBodyInput<typeof jupyterUploadContract>
export type JupyterUploadResponse = ContractJsonResponse<typeof jupyterUploadContract>
export type SshCheckCommandExistsBody = ContractBodyInput<typeof sshCheckCommandExistsContract>
export type SshCheckFileExistsBody = ContractBodyInput<typeof sshCheckFileExistsContract>
export type SshCreateDirectoryBody = ContractBodyInput<typeof sshCreateDirectoryContract>
export type SshDeleteFileBody = ContractBodyInput<typeof sshDeleteFileContract>
export type SshDownloadFileBody = ContractBodyInput<typeof sshDownloadFileContract>
export type SshExecuteCommandBody = ContractBodyInput<typeof sshExecuteCommandContract>
export type SshExecuteScriptBody = ContractBodyInput<typeof sshExecuteScriptContract>
export type SshGetSystemInfoBody = ContractBodyInput<typeof sshGetSystemInfoContract>
export type SshListDirectoryBody = ContractBodyInput<typeof sshListDirectoryContract>
export type SshMoveRenameBody = ContractBodyInput<typeof sshMoveRenameContract>
export type SshReadFileContentBody = ContractBodyInput<typeof sshReadFileContentContract>
export type SshUploadFileBody = ContractBodyInput<typeof sshUploadFileContract>
export type SshWriteFileContentBody = ContractBodyInput<typeof sshWriteFileContentContract>
export type FileParseBody = ContractBodyInput<typeof fileParseContract>
export type FileParseResponse = ContractJsonResponse<typeof fileParseContract>
export type FileDeleteBody = ContractBodyInput<typeof fileDeleteContract>
export type FileDeleteResponse = ContractJsonResponse<typeof fileDeleteContract>
export type FileServeParams = ContractParamsInput<typeof fileServeContract>
export type FileServeQuery = ContractQueryInput<typeof fileServeContract>
export type FileViewParams = ContractParamsInput<typeof fileViewContract>
export type FileExportParams = ContractParamsInput<typeof fileExportContract>
